/**
 * Task and feed text (plan §19.4). Trigger tasks are titled from their trigger and inputs in the UI language
 * (the API stores an English title for API clients and e-mails build their own); people's tasks keep what was typed.
 */
import type { I18n } from '@/i18n';
import type { FeedItem, Task, TaskStatus, TaskTrigger } from '@/lib/api/types';

const str = (v: unknown) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');

/** Variables for a trigger's title, from the task's inputs. */
export function triggerVars(inputs: Record<string, unknown>) {
  return {
    episode: str(inputs.episode_title) || str(inputs.export_id).slice(0, 8),
    video: str(inputs.video_title),
    version: str(inputs.version) || '1',
    customer: str(inputs.customer),
    platform: str(inputs.platform),
  };
}

export function taskTitle(i: Pick<I18n, 'td'>, task: { trigger: TaskTrigger | null; title: string; inputs: Record<string, unknown> }): string {
  if (!task.trigger) return task.title;
  return i.td(`trg_${task.trigger}`, task.title, triggerVars(task.inputs));
}

/** The inbox column a status belongs to: cancelled and failed tasks sit with Done. */
export type TaskColumn = 'todo' | 'in_progress' | 'needs_you' | 'done';
export const TASK_COLUMNS: TaskColumn[] = ['todo', 'in_progress', 'needs_you', 'done'];
export const columnOf = (s: TaskStatus): TaskColumn => (s === 'todo' || s === 'in_progress' || s === 'needs_you' ? s : 'done');

/** Where a task's target lives in the app. */
export function targetLink(task: Pick<Task, 'project' | 'episode' | 'edit'>): { to: string; kind: 'video' | 'episode' | 'project' } | null {
  if (task.edit) return { to: `/approvals/${task.edit.id}`, kind: 'video' };
  if (task.episode && task.project) return { to: `/projects/${task.project.id}/episodes/${task.episode.id}`, kind: 'episode' };
  if (task.project) return { to: `/projects/${task.project.id}`, kind: 'project' };
  return null;
}

/** One feed line in the UI language. */
export function feedText(i: Pick<I18n, 't' | 'td'>, item: FeedItem): string {
  const actor = item.actor.kind === 'system' || !item.actor.name ? i.t('actSystem') : item.actor.name;
  const task = item.task ? taskTitle(i, item.task) : '';
  const vars = {
    actor,
    video: item.edit?.title || item.episode?.title || '',
    episode: item.episode ? `${item.episode.number ? `${item.episode.number} · ` : ''}${item.episode.title}` : '',
    project: item.project?.title ?? '',
    task,
    status: typeof item.meta.to === 'string' ? i.td(`tkStatus_${item.meta.to}`, item.meta.to) : '',
    to: '',
  };
  const key = `act_${item.action.replace(/\./g, '_')}`;
  if (item.actor.kind === 'system' && (item.action === 'task.created' || item.action === 'task.status')) return i.td(`${key}_system`, '', vars);
  return i.td(key, item.action, vars);
}
